import { c as _c } from "react/compiler-runtime";
import type { Condition, Provider } from '@repo/api-gw-sdk';
import { type IngestionPipelineTag, LakehouseApi } from '@repo/lakehouse-sdk';
import { useEffect, useState } from 'react';
import { useDAL } from '@/contexts/useDAL';
import { useNonProjectDal } from '@/contexts/useNonProjectDal';
import { useUser } from '@/contexts/useUser';
import type { ConditionPanelHost } from '@/types/advanceFilter';
import type { TagFilterMode } from './tagKeyValueUtils';
import type { FilterProps } from '../filters';
export interface TagsConditionProps extends FilterProps {
  supportedProviders?: Provider[];
  initialFilterMode: TagFilterMode;
  panelHost?: ConditionPanelHost;
  /** Runs whenever the popover closes, hosted or not — for the wrapper's own cleanup. */
  onPanelClose?: () => void;
  dataSource?: 'tags' | 'sourceAccountTags' | 'pipelineTags';
}
export interface TagsDataResult {
  data: {
    tags?: {
      key: string;
      value?: string | null;
    }[];
  } | undefined;
  isLoading: boolean;
}
export function useResourceTagsData(filter?: Condition): TagsDataResult {
  const dal = useDAL();
  return dal.inventory.tags.useList(filter);
}
export function useSourceAccountTagsData(filter?: Condition): TagsDataResult {
  const dal = useDAL();
  return dal.inventory.sourceAccountTags.useList(filter);
}

// Bounds the suggestions to 1,000 distinct pairs; a tag past them can still be typed in.
const MAX_PIPELINE_TAG_PAGES = 5;
const listAllPipelineTags = async (api: LakehouseApi, projectId: string): Promise<TagsDataResult['data']> => {
  const tags: IngestionPipelineTag[] = [];
  let pageToken: string | undefined;
  for (let page = 0; page < MAX_PIPELINE_TAG_PAGES; page++) {
    const response = await api.listIngestionPipelineTags(projectId, pageToken, 200);
    tags.push(...response.tags);
    pageToken = response.nextToken ?? undefined;
    if (!pageToken) {
      break;
    }
  }
  return {
    tags
  };
};

// Pipeline tags live in lakehouse, not the inventory. The role editor sits outside the lakehouse
// apps, so it builds the one client it needs rather than mounting the lakehouse DAL. Suggestions
// are optional: a failed lookup leaves the picker to typed-in values.
export function usePipelineTagsData() {
  const $ = _c(8);
  const {
    lakehouseConfiguration
  } = useNonProjectDal();
  const {
    currentProjectId
  } = useUser();
  const [loaded, setLoaded] = useState();
  let t0;
  let t1;
  if ($[0] !== currentProjectId || $[1] !== lakehouseConfiguration) {
    t0 = () => {
      if (!currentProjectId) {
        return;
      }
      let current = true;
      listAllPipelineTags(new LakehouseApi(lakehouseConfiguration), currentProjectId).catch(_temp).then(data => {
        if (current) {
          setLoaded({
            projectId: currentProjectId,
            data
          });
        }
      });
      return () => {
        current = false;
      };
    };
    t1 = [lakehouseConfiguration, currentProjectId];
    $[0] = currentProjectId;
    $[1] = lakehouseConfiguration;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  if (!currentProjectId) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        data: {
          tags: []
        },
        isLoading: false
      };
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] !== currentProjectId || $[6] !== loaded) {
    t2 = loaded?.projectId === currentProjectId ? {
      data: loaded.data,
      isLoading: false
    } : {
      data: undefined,
      isLoading: true
    };
    $[5] = currentProjectId;
    $[6] = loaded;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function _temp() {
  return {
    tags: []
  };
}